import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MainLayout from "../components/MainLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import Loader from "../components/ui/Loader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { apiGet, apiPost } from "../lib/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const TesterHubPage = () => {
	const { user } = useAuth();
	const { toast } = useToast();
	const [searchParams, setSearchParams] = useSearchParams();

	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const [submissions, setSubmissions] = useState([]);
	const [pagination, setPagination] = useState({});

	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [tags, setTags] = useState("");
	const [apkVersion, setApkVersion] = useState("");
	const [apkFile, setApkFile] = useState(null);
	const [mediaFiles, setMediaFiles] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const page = useMemo(() => Number(searchParams.get("page") || 1), [searchParams]);
	const limit = 12;

	const fetchSubmissions = async () => {
		setIsLoading(true);
		setError("");
		try {
			const response = await apiGet(`/tester-hub/submissions?page=${page}&limit=${limit}`);
			setSubmissions(Array.isArray(response?.submissions) ? response.submissions : []);
			setPagination(response?.pagination || {});
		} catch (err) {
			setError(err.message || "Failed to load submissions");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchSubmissions();
	}, [page]);

	const setPage = (nextPage) => {
		const next = new URLSearchParams(searchParams);
		if (!nextPage || nextPage === 1) next.delete("page");
		else next.set("page", String(nextPage));
		setSearchParams(next);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!user) {
			toast({
				title: "Login required",
				description: "Please log in to submit a build for testing.",
				variant: "destructive",
			});
			return;
		}

		setIsSubmitting(true);
		try {
			const formData = new FormData();
			formData.append("title", title);
			formData.append("description", description);
			formData.append("tags", tags);
			formData.append("apkVersion", apkVersion);
			if (apkFile) formData.append("apkFile", apkFile);
			for (const file of mediaFiles) formData.append("screenshots", file);

			const response = await apiPost("/tester-hub/submissions", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			toast({
				title: "Submitted",
				description: response?.message || "Your submission is live.",
				variant: "success",
			});

			setTitle("");
			setDescription("");
			setTags("");
			setApkVersion("");
			setApkFile(null);
			setMediaFiles([]);

			await fetchSubmissions();
		} catch (err) {
			toast({
				title: "Submit failed",
				description: err.message || "Could not create submission.",
				variant: "destructive",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<MainLayout>
			<main>
				<section className="rounded-2xl sm:rounded-3xl bg-card shadow-card border border-border p-4 sm:p-6 mb-6">
					<Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Tester Hub" }]} />
					<div className="mt-2 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
						<div>
							<h1 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-foreground">Tester Hub</h1>
							<p className="text-muted-foreground mt-1 sm:mt-2 max-w-2xl text-sm sm:text-base">
								Developers can share an APK or a game overview with screenshots / video, and testers can report whether it works or not.
							</p>
						</div>
						<Button asChild variant="secondary">
							<Link to="/tester-hub/apps">Browse testing apps</Link>
						</Button>
					</div>
				</section>

				<Card className="mb-8">
					<CardHeader>
						<CardTitle>Submit a build</CardTitle>
					</CardHeader>
					<form onSubmit={handleSubmit}>
						<CardContent>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="text-sm text-muted-foreground">Title</label>
									<Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="My new game" />
								</div>
								<div>
									<label className="text-sm text-muted-foreground">APK version (optional)</label>
									<Input value={apkVersion} onChange={(e) => setApkVersion(e.target.value)} placeholder="1.0.3" />
								</div>
							</div>

							<div className="mt-4">
								<label className="text-sm text-muted-foreground">Description</label>
								<Textarea
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="What should testers focus on? Known issues? How to reproduce?"
									rows={5}
								/>
							</div>

							<div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="text-sm text-muted-foreground">Tags (comma separated)</label>
									<Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="android, puzzle, offline" />
								</div>
								<div>
									<label className="text-sm text-muted-foreground">APK file (optional)</label>
									<Input
										type="file"
										accept=".apk,application/vnd.android.package-archive"
										onChange={(e) => setApkFile(e.target.files?.[0] || null)}
									/>
								</div>
							</div>

							<div className="mt-4">
								<label className="text-sm text-muted-foreground">Screenshots / video trailer (optional)</label>
								<Input
									type="file"
									multiple
									accept="image/*,video/*"
									onChange={(e) => setMediaFiles(Array.from(e.target.files || []))}
								/>
								{mediaFiles?.length ? (
									<p className="text-xs text-muted-foreground mt-2">
										{mediaFiles.length} file(s) selected
									</p>
								) : null}
							</div>
						</CardContent>
						<CardFooter className="flex items-center justify-between gap-3">
							<p className="text-xs text-muted-foreground">
								{user ? "" : "Log in to submit a build."}
							</p>
							<Button type="submit" disabled={isSubmitting || !title.trim()}>
								{isSubmitting ? "Submitting..." : "Submit"}
							</Button>
						</CardFooter>
					</form>
				</Card>

				{isLoading ? (
					<Loader label="Loading submissions" />
				) : error ? (
					<p className="text-red-500">{error}</p>
				) : submissions.length ? (
					<>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{submissions.map((s) => {
								const id = s._id || s.id;
								return (
									<Card key={id} className="h-full">
										<CardHeader>
											<CardTitle className="normal-case tracking-normal text-primary">
												{s.title}
											</CardTitle>
										</CardHeader>
										<CardContent>
											<p className="text-sm text-muted-foreground line-clamp-3">
												{s.description || "No description"}
											</p>
											<div className="mt-3 text-xs text-muted-foreground flex flex-wrap gap-2">
												<span>By {s.createdBy?.name || "Unknown"}</span>
												{s.apkUrl ? <span>• APK</span> : <span>• No APK</span>}
												<span>
													• {s.feedbackCounts?.working || 0} working / {s.feedbackCounts?.notWorking || 0} not working
												</span>
											</div>
										</CardContent>
										<CardFooter className="flex items-center justify-end">
											<Button asChild size="sm" variant="secondary">
												<Link to={`/tester-hub/${id}`}>Open</Link>
											</Button>
										</CardFooter>
									</Card>
								);
							})}
						</div>

						<div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mt-10">
							<Button size="sm" className="w-full sm:w-auto" disabled={page <= 1} onClick={() => setPage(page - 1)}>
								Previous
							</Button>
							<span className="text-sm text-muted-foreground">Page {page}</span>
							<Button
								size="sm"
								className="w-full sm:w-auto"
								disabled={!pagination?.hasNextPage}
								onClick={() => setPage(page + 1)}
							>
								Next
							</Button>
						</div>
					</>
				) : (
					<div className="text-center space-y-3 px-4">
						<p className="text-muted-foreground">No submissions yet.</p>
					</div>
				)}
			</main>
		</MainLayout>
	);
};

export default TesterHubPage;
